import type { MedalItem } from "@/api/types";
import { cn } from "@/lib/cn";
import { MedalTooltip } from "./MedalTooltip";

export interface MedalTrayProps {
  medals: MedalItem[];
  /** Visual size of each slot. Defaults to "md". */
  size?: "sm" | "md";
  className?: string;
}

const SIZE_MAP = {
  sm: {
    slot: "h-8 w-8",
    emoji: "text-base",
    ring: 14, // svg radius
    stroke: 2,
    svg: 32,
  },
  md: {
    slot: "h-10 w-10",
    emoji: "text-lg",
    ring: 18,
    stroke: 2,
    svg: 40,
  },
} as const;

/**
 * Rarity heuristic — all earned medals share the same gold treatment today
 * (backend does not yet tag rarity). We keep a slot for per-medal rarity
 * mapping once the API exposes it.
 */
function rarityTone(_medal: MedalItem): "gold" | "silver" | "bronze" {
  return "gold";
}

const TONE_CLASSES: Record<
  "gold" | "silver" | "bronze",
  { bg: string; ring: string; glow: string; emoji: string }
> = {
  gold: {
    bg: "bg-gradient-to-br from-gold-1/25 to-gold-2/10",
    ring: "ring-gold-2/50",
    glow: "shadow-[0_0_16px_-4px_rgba(251,191,36,0.55)]",
    emoji: "drop-shadow-[0_0_6px_rgba(251,191,36,0.55)]",
  },
  silver: {
    bg: "bg-gradient-to-br from-silver-2/25 to-white/5",
    ring: "ring-silver-2/45",
    glow: "shadow-[0_0_16px_-4px_rgba(203,213,225,0.45)]",
    emoji: "drop-shadow-[0_0_6px_rgba(203,213,225,0.45)]",
  },
  bronze: {
    bg: "bg-gradient-to-br from-bronze-2/25 to-bronze-2/5",
    ring: "ring-bronze-2/45",
    glow: "shadow-[0_0_16px_-4px_rgba(217,119,6,0.45)]",
    emoji: "drop-shadow-[0_0_6px_rgba(217,119,6,0.45)]",
  },
};

/** Full catalog of medals rendered inline — one small glass slot per medal.
 * Earned medals bloom with color + glow + subtle float. Locked medals dim
 * to grayscale with a thin SVG ring indicating progress. Each slot is a
 * hover trigger for MedalTooltip. */
export function MedalTray({
  medals,
  size = "md",
  className,
}: MedalTrayProps) {
  if (medals.length === 0) {
    return (
      <div className={cn("text-[11px] text-text-4", className)}>
        Sin medallas
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-1.5",
        className,
      )}
      role="list"
      aria-label="Medallas"
    >
      {medals.map((m) => (
        <MedalTooltip key={m.code} medal={m}>
          <MedalSlot medal={m} size={size} />
        </MedalTooltip>
      ))}
    </div>
  );
}

function MedalSlot({
  medal,
  size,
}: {
  medal: MedalItem;
  size: "sm" | "md";
}) {
  const dims = SIZE_MAP[size];
  const tone = rarityTone(medal);
  const toneCls = TONE_CLASSES[tone];

  if (medal.unlocked) {
    return (
      <div
        role="listitem"
        aria-label={`${medal.label} — conseguida`}
        className={cn(
          "relative flex flex-shrink-0 items-center justify-center rounded-full ring-1",
          "motion-safe:animate-[medalFloat_3.6s_ease-in-out_infinite]",
          dims.slot,
          toneCls.bg,
          toneCls.ring,
          toneCls.glow,
        )}
        style={{ animationDelay: `${(medal.code.length % 5) * 0.15}s` }}
      >
        <span
          aria-hidden
          className={cn("leading-none", dims.emoji, toneCls.emoji)}
        >
          {medal.emoji}
        </span>
      </div>
    );
  }

  return (
    <LockedSlot
      medal={medal}
      slotCls={dims.slot}
      emojiCls={dims.emoji}
      svgSize={dims.svg}
      ringRadius={dims.ring}
      strokeWidth={dims.stroke}
    />
  );
}

function LockedSlot({
  medal,
  slotCls,
  emojiCls,
  svgSize,
  ringRadius,
  strokeWidth,
}: {
  medal: MedalItem;
  slotCls: string;
  emojiCls: string;
  svgSize: number;
  ringRadius: number;
  strokeWidth: number;
}) {
  const pct = Math.max(0, Math.min(100, medal.pct));
  const circ = 2 * Math.PI * ringRadius;
  const dashOffset = circ * (1 - pct / 100);
  const cx = svgSize / 2;
  const cy = svgSize / 2;

  return (
    <div
      role="listitem"
      aria-label={`${medal.label} — bloqueada, ${pct.toFixed(0)}% de progreso`}
      className={cn(
        "relative flex flex-shrink-0 items-center justify-center rounded-full bg-white/[0.03] ring-1 ring-border-2",
        slotCls,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "leading-none opacity-45 grayscale",
          emojiCls,
        )}
      >
        {medal.emoji}
      </span>

      {/* Thin progress ring. */}
      <svg
        aria-hidden
        width={svgSize}
        height={svgSize}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        className="pointer-events-none absolute inset-0"
      >
        <circle
          cx={cx}
          cy={cy}
          r={ringRadius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={cx}
          cy={cy}
          r={ringRadius}
          fill="none"
          stroke="url(#medalTrayProgress)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${cx} ${cy})`}
          className="motion-reduce:transition-none transition-[stroke-dashoffset] duration-500"
        />
        <defs>
          <linearGradient id="medalTrayProgress" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--color-brand-1)" />
            <stop offset="100%" stopColor="var(--color-brand-3)" />
          </linearGradient>
        </defs>
      </svg>

      {/* Lock chip overlay in the bottom-right corner. */}
      <span
        aria-hidden
        className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-border-2 bg-bg-1 text-[8px] text-text-3"
      >
        <LockIcon />
      </span>
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 10 10"
      width="7"
      height="7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="4.5" width="6" height="4" rx="1" />
      <path d="M3.4 4.5V3.2a1.6 1.6 0 0 1 3.2 0v1.3" />
    </svg>
  );
}
